import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import {
  IPageInfo,
  VirtualScrollerComponent,
  VirtualScrollerModule,
} from '@iharbeck/ngx-virtual-scroller';

import { Attribute, AttributeListData } from '@recruitee/http/types';

import { DropdownItemComponent } from '../../common/dropdown/dropdown-item/dropdown-item.component';
import { ProgressBarComponent } from '../../common/progress-bar/progress-bar.component';
import { FocusTrapDeprecatedDirective } from '../../core/a11y/focus-trap';
import { provideIntersectionObserverArea } from '../../intersection-observer/intersection-observer-area';

export interface AttributeVisibilityChange {
  isVisible: boolean;
  attribute: Attribute;
  index: number;
}

@Component({
  selector: 'rt-attributes-select-list',
  styleUrls: ['./attributes-select-list.component.less'],
  template: `
    <ng-template #item let-attr>
      <rt-dropdown-item
        [highlighted]="highlightedId === attr.id"
        (click)="saveAttribute.emit({ value: attr.value, id: attr.id })"
      >
        {{ attr.id !== createNewItemId ? attr.value : null }}
        @if (attr.id === createNewItemId) {
          <ng-container [ngTemplateOutlet]="createNewAttr"></ng-container>
        }
        <span suffix class="counter">{{ attr.count }}</span>
      </rt-dropdown-item>
    </ng-template>

    <ng-template #noVirtualScrollTmpl>
      @for (attr of viewportItems; track trackByAttribute(i, attr); let i = $index) {
        <ng-container [ngTemplateOutlet]="item" [ngTemplateOutletContext]="{ $implicit: attr }">
        </ng-container>
      }
    </ng-template>

    <ng-template #virtualScrollTmpl>
      <div
        #scroll
        virtualScroller
        [bufferAmount]="5"
        [scrollAnimationTime]="0"
        [scrollThrottlingTime]="10"
        [enableUnequalChildrenSizes]="true"
        [executeRefreshOutsideAngularZone]="true"
        [items]="suggestionsListData.rows"
        (vsUpdate)="onVirtualScrollUpdate($event)"
        (vsChange)="onVirtualScrollChange($event)"
        [style.height]="maxHeight"
        class="scroll"
      >
        <div #container>
          <ng-container [ngTemplateOutlet]="noVirtualScrollTmpl"></ng-container>
        </div>
        @if (isLoading) {
          <rt-progress-bar></rt-progress-bar>
        }
      </div>
    </ng-template>

    <rt-focus-trap>
      <div #scrollContainer [style.max-height]="maxHeight">
        <ng-container
          [ngTemplateOutlet]="virtualScrollEnabled ? virtualScrollTmpl : noVirtualScrollTmpl"
        ></ng-container>
      </div>
    </rt-focus-trap>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...provideIntersectionObserverArea()],
  standalone: true,
  imports: [
    DropdownItemComponent,
    FocusTrapDeprecatedDirective,
    NgTemplateOutlet,
    ProgressBarComponent,
    VirtualScrollerModule,
  ],
})
export class AttributesSelectListComponent {
  @Input() public suggestionsListData: AttributeListData;
  @Input() public highlightedId: string | number | null;
  @Input() public createNewItemId: string;
  @Input() public createNewAttr: TemplateRef<any>;
  @Input() public maxHeight: string = '300px';

  public viewportItems: AttributeListData['rows'] = [];
  public isLoading: boolean = true;
  public virtualScrollEnabled: boolean;

  @Output() public saveAttribute: EventEmitter<Attribute> = new EventEmitter<Attribute>();
  @Output() public loadMore = new EventEmitter<void>();

  @ViewChild('scroll', { static: false }) private virtualScroll: VirtualScrollerComponent | null;
  @ViewChild('scrollContainer', { static: true }) private scrollContainer: ElementRef;

  private readonly REQUEST_NEW_DATA = 20;
  private currentPage: number = 0;
  private canRequestData: boolean = true;

  constructor(private cdRef: ChangeDetectorRef) {}

  public onVirtualScrollUpdate(visibleItems: AttributeListData['rows']): void {
    this.viewportItems = visibleItems;
    this.cdRef.detectChanges();
  }

  public onVirtualScrollChange(event: IPageInfo): void {
    this.onVisibilityChange({
      attribute: this.viewportItems[event.endIndex],
      isVisible: true,
      index: event.endIndex,
    });
  }

  public trackByAttribute(index: number, attr: Attribute): string {
    return attr.value;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.virtualScrollEnabled = this.suggestionsListData.meta!.totalCount > 20;
    if (!this.virtualScrollEnabled) {
      this.viewportItems = this.suggestionsListData.rows;
    }

    if ('highlightedId' in changes) {
      if (this.virtualScroll) {
        this.virtualScroll.scrollToIndex(0);
      } else {
        this.scrollContainer.nativeElement.scrollTop = 0;
      }
    }
    if ('suggestionsListData' in changes) {
      const { page } = this.suggestionsListData.meta!;
      if (this.currentPage !== page) {
        this.currentPage = page;
        this.isLoading = false;
        this.canRequestData = true;
      }
    }
  }

  public onVisibilityChange(event: AttributeVisibilityChange) {
    const { page, limit, totalCount } = this.suggestionsListData.meta!;
    const leftToSeeCount = this.suggestionsListData.rows.length - event.index;

    const allDataLoaded = Math.ceil(totalCount / limit) === page;
    const shouldRequestData =
      event.isVisible &&
      this.canRequestData &&
      !allDataLoaded &&
      leftToSeeCount < this.REQUEST_NEW_DATA;

    if (shouldRequestData) {
      this.canRequestData = false;
      this.loadMore.emit();
      this.isLoading = true;
    }
  }
}
